import React, { useEffect, useState } from "react";
import {
  calculatePresentValue,
  calculateFutureValue,
  calculatePeriodicPayment,
  rmdDistributionPeriods,
  stateIncomeTaxRates,
  calculateFederalIncomeTax,
  allocateSavings,
} from "./retirementGoalCalculations";
import { calculateTotalTax } from "./taxCalculations";
import { formatCurrencyAmount } from "./formatAmount";
import WithdrawalStrategyChart from "./WithdrawalStrategyChart";

export default function RetirementProjection({
  payload = {},
  taxInfo,
  taxInfoAllData,
  rateOfReturnData,
  setPayload,
  assetsAssign = [], // ADD THIS PROP for illiquid asset handling
}) {
  const [yearsUntilRetirement, setYearsUntilRetirement] = useState(0);
  const [netAfterTax, setNetAfterTax] = useState(0);
  const [savingsAllocationMonthly, setSavingsAllocationMonthly] = useState({
    deferred: 0,
    free: 0,
    taxable: 0,
  });
  const [userEstimatedValue, setUserEstimatedValue] = useState(0);
  const [goalsValue, setGoalsValue] = useState(0);
  const [userShortfall, setUserShortfall] = useState(0);
  const [userMonthly, setUserMonthly] = useState(0);

  const [projectionTable, setProjectionTable] = useState([]);
  const [proposedMonthlySavings, setProposedMonthlySavings] = useState(null);

  const getNum = (key, fallback = 0) => {
    const v = payload && payload[key];
    if (v === undefined || v === null || v === "") return fallback;
    return typeof v === "number"
      ? v
      : parseFloat(String(v).replace(/[^0-9.\-]/g, "")) || fallback;
  };

  function getSuggestedSavings(targetAccount, allocationMonthly) {
    if (!targetAccount) return 0;

    if (targetAccount.includes("Tax-Deferred"))
      return allocationMonthly.deferred;
    if (targetAccount.includes("Roth")) return allocationMonthly.free;
    if (targetAccount.includes("Brokerage")) return allocationMonthly.taxable;

    return 0;
  }

  function hasChanges(oldPayload, updates) {
    return Object.keys(updates).some((key) => {
      const oldVal = oldPayload[key];
      const newVal = updates[key];
      return String(oldVal) !== String(newVal);
    });
  }

  useEffect(() => {
    if (!payload) return;

    // ----------------------------
    // SAFE GETTER (NO ?? USED)
    // ----------------------------
    function getVal(key, fallback) {
      const v = payload && payload[key];
      return v !== undefined && v !== null && v !== "" ? Number(v) : fallback;
    }

    // === Extract Inputs ===
    const currentAge = getVal("Current Age", 0);
    const desiredRetAge = getVal("Desired Retirement Age", 0);
    const desiredRetAgeSpouse = getVal("Spouse's Desired Retirement Age", 0);

    const lifeExpectancy = getVal("Client Life Expectancy", 0);

    const annualPostRetBudget = getVal("Net Annual Post-Retirement Budget", 0);

    // let inflRaw = payload["Inflation rate for this goal"];
    // let inflationRate = 0;
    // if (inflRaw !== undefined && inflRaw !== null && inflRaw !== "") {
    //   inflRaw = String(inflRaw).replace(/[^0-9.\-]/g, "");
    //   inflationRate = parseFloat(inflRaw);
    //   if (inflationRate > 1) inflationRate = inflationRate / 100;
    // }
    const inflationInput = payload && payload["Inflation rate for this goal"];
    let inflationRate = 0;
    const matchedInflation = taxInfo && taxInfo.find((item) => item.name === inflationInput);

    if (matchedInflation) {
      inflationRate = (parseFloat(matchedInflation.value) || 0) / 100;
    } else if (inflationInput !== undefined && inflationInput !== null && inflationInput !== "") {
      const inflRaw = String(inflationInput).replace(/[^0-9.\-]/g, "");
      const parsed = parseFloat(inflRaw);
      if (!isNaN(parsed)) {
        inflationRate = parsed > 1 ? parsed / 100 : parsed;
      }
    }

    // Calculate Weighted Average Return from Assets if available
    let totalAssetsBal = 0;
    let totalAssetsReturnNum = 0;
    if (Array.isArray(assetsAssign) && assetsAssign.length > 0) {
      assetsAssign.forEach(a => {
        if (a.listType === "liquid") {
          const bal = parseFloat(a.valueOfAssetGoalRate || a["Monetary Value"]) || 0;
          const r = parseFloat(a.rateOfReturn) || 0;
          totalAssetsBal += bal;
          totalAssetsReturnNum += bal * r;
        }
      });
    }
    const returnRateFromAssets = totalAssetsBal > 0 ? (totalAssetsReturnNum / totalAssetsBal) : null;

    const returnRate = (returnRateFromAssets !== null)
      ? returnRateFromAssets
      : (payload && payload["Assumed Annual Investment Return (%)"] !== undefined && payload["Assumed Annual Investment Return (%)"] !== "")
        ? payload["Assumed Annual Investment Return (%)"]
        : (rateOfReturnData && rateOfReturnData.staticPercentage);

    const annualReturnRate =
      returnRate !== undefined && returnRate !== null && returnRate !== ""
        ? Number(returnRate) / 100
        : 0.06; // Default to 6% if not set anywhere, instead of 0%
    // console.log("Annual Return Rate:", annualReturnRate,returnRate);
    const ssUser = getVal("Your Estimated Social Security", 0);
    const ssSpouse = getVal("Social Security - Spouse", 0);
    const pension = getVal("Pension", 0);
    const rental = getVal("Net Rental Income", 0);

    let additionalIncomes = [];
    if (Array.isArray(payload["Future Value (at Retirement Age)"])) {
      additionalIncomes = payload["Future Value (at Retirement Age)"];
    } else if (payload["Future Value (at Retirement Age)"]) {
      additionalIncomes = [payload["Future Value (at Retirement Age)"]];
    } else if (Array.isArray(payload["Annual Amount"])) {
      // Fallback for older data or if Future Value isn't populated yet
      additionalIncomes = payload["Annual Amount"];
    } else if (payload["Annual Amount"]) {
      additionalIncomes = [payload["Annual Amount"]];
    }


    const yearsToRetirement = Math.max(0, desiredRetAge - currentAge);
    const yearsInRetirement = Math.max(0, lifeExpectancy - desiredRetAge);

    // ----------------------------
    // INITIAL BALANCES AGGREGATION
    // ----------------------------
    let taxableBalance = getVal("taxableBalance", 0);
    let taxDeferredBalance = getVal("taxDeferredBalance", 0);
    let taxFreeBalance = getVal("taxFreeBalance", 0);

    let taxableContrib = getVal("taxableContrib", 0);
    let taxDeferredContrib = getVal("taxDeferredContrib", 0);
    let taxFreeContrib = getVal("taxFreeContrib", 0);

    // Recalculate from assetsAssign if missing or zero (Initial Load Support)
    if (Array.isArray(assetsAssign) && assetsAssign.length > 0) {
      let calcTaxableBal = 0, calcDeferredBal = 0, calcFreeBal = 0;
      let calcTaxableCont = 0, calcDeferredCont = 0, calcFreeCont = 0;

      assetsAssign.forEach(row => {
        if (row.listType === "liquid") {
          const bal = parseFloat(row.valueOfAssetGoalRate || row["Monetary Value"]) || 0;
          const contrib = parseFloat(row.annualSavings) || 0;
          const status = row.taxStatus || "";

          if (status === "Roth IRA (Tax-Free)") {
            calcFreeBal += bal;
            calcFreeCont += contrib;
          } else if (status === "Tax-Deferred 401k") {
            calcDeferredBal += bal;
            calcDeferredCont += contrib;
          } else {
            calcTaxableBal += bal;
            calcTaxableCont += contrib;
          }
        }
      });

      // Use calculated values if they are non-zero (or if payload values are zero)
      if (taxableBalance === 0 && calcTaxableBal !== 0) taxableBalance = calcTaxableBal;
      if (taxDeferredBalance === 0 && calcDeferredBal !== 0) taxDeferredBalance = calcDeferredBal;
      if (taxFreeBalance === 0 && calcFreeBal !== 0) taxFreeBalance = calcFreeBal;

      if (taxableContrib === 0 && calcTaxableCont !== 0) taxableContrib = calcTaxableCont;
      if (taxDeferredContrib === 0 && calcDeferredCont !== 0) taxDeferredContrib = calcDeferredCont;
      if (taxFreeContrib === 0 && calcFreeCont !== 0) taxFreeContrib = calcFreeCont;
    }

    const spouseAge = getVal("Spouse's Current Age", 0);
    const filingStatus = payload["Tax Filing Status"] || "";

    const ssUserStartAge = getVal("Your Estimated Social Security Start Age", desiredRetAge) || getVal("Your Desired Claim Age", desiredRetAge);
    const ssSpouseStartAge = getVal("Social Security - Spouse Start Age", desiredRetAgeSpouse) || getVal("Spouse's Desired Retirement Age", desiredRetAge);
    // Difference between user and spouse current ages
    const ageDiff = currentAge - spouseAge;

    const state =
      payload["State of Residence in Retirement"] ||
      payload["state"] ||
      "";

    // ----------------------------
    // PROJECTED VALUE UNTIL RETIREMENT
    // ----------------------------
    const currentTotalBalance =
      taxableBalance + taxDeferredBalance + taxFreeBalance;
    const currentTotalContrib =
      taxableContrib + taxDeferredContrib + taxFreeContrib;

    const projectedValue = calculateFutureValue(
      annualReturnRate,
      yearsToRetirement,
      -currentTotalContrib,
      -currentTotalBalance
    );

    // ----------------------------
    // SAVINGS GOAL IN TODAYS DOLLARS
    // ----------------------------
    const realRateOfReturn =
      (annualReturnRate - inflationRate) / (1 + inflationRate);

    const totalSavingsGoalAtRetirementInTodaysDollars = calculatePresentValue(
      realRateOfReturn,
      yearsInRetirement,
      -annualPostRetBudget,
      0
    );

    const totalSavingsGoalNominal =
      totalSavingsGoalAtRetirementInTodaysDollars *
      Math.pow(1 + inflationRate, yearsToRetirement);

    const finalShortfall = Math.max(
      0,
      totalSavingsGoalNominal - projectedValue
    );

    const requiredMonthly =
      finalShortfall > 0
        ? Math.max(
          0,
          -calculatePeriodicPayment(
            annualReturnRate / 12,
            Math.max(1, yearsToRetirement * 12),
            0,
            finalShortfall
          )
        )
        : 0;

    // ----------------------------
    // SET BASE VALUES
    // ----------------------------
    const userProposedMonthlyFromPayload = getNum("Your Commitment", NaN);
    // FIXED proposedMonthly
    let proposedMonthly = 0;

    if (
      userProposedMonthlyFromPayload !== undefined &&
      userProposedMonthlyFromPayload !== null &&
      userProposedMonthlyFromPayload !== "" &&
      !isNaN(Number(userProposedMonthlyFromPayload))
    ) {
      proposedMonthly = Number(userProposedMonthlyFromPayload);
    } else {
      proposedMonthly = requiredMonthly;
    }

    // FIXED allocationMonthly
    const allocationMonthly = allocateSavings(proposedMonthly);
    // console.log(
    //   "Allocation Monthly:",
    //   payload["Target Account"],
    //   proposedMonthly,
    //   allocationMonthly,
    //   userProposedMonthlyFromPayload,
    //   requiredMonthly
    // );

    // Determine Suggested Savings for chosen account
    const targetAccount = payload["Target Account"];
    const suggestedSavings = getSuggestedSavings(
      targetAccount,
      allocationMonthly
    );

    // Remaining gap logic
    const remainingGap = Math.max(0, requiredMonthly - proposedMonthly);

    console.log("projjjejeje", projectedValue);
    setSavingsAllocationMonthly(allocationMonthly);
    setYearsUntilRetirement(yearsToRetirement);
    setUserEstimatedValue(projectedValue);
    setGoalsValue(totalSavingsGoalNominal);
    setUserShortfall(finalShortfall);
    setUserMonthly(requiredMonthly);

    // -------------------------------------------------------
    // RETIREMENT SPENDING PROJECTION TABLE
    // -------------------------------------------------------
    function runFullProjection() {
      // ===================================================================
      // PHASE 1: INITIALIZATION (Using Future Value from Assets)
      // ===================================================================

      // Initialize account balances at retirement using the Future Value calculated in the form
      let taxableStartBal = 0;
      let taxDeferredStartBal = 0;
      let taxFreeStartBal = 0;

      let taxableReturnSum = 0;
      let taxDeferredReturnSum = 0;
      let taxFreeReturnSum = 0;

      // Get illiquid assets that might be sold
      const illiquidAssets = Array.isArray(assetsAssign)
        ? assetsAssign.filter(a => a.listType === "illiquid")
        : [];

      const startYear = new Date().getFullYear();

      if (Array.isArray(assetsAssign) && assetsAssign.length > 0) {
        assetsAssign.forEach(asset => {
          if (asset.listType === "liquid") {
            const fv = parseFloat(asset.futureValue) || 0;
            const r = (parseFloat(asset.rateOfReturn) || 0) / 100;
            const status = asset.taxStatus || "";

            if (status === "Roth IRA (Tax-Free)") {
              taxFreeStartBal += fv;
              taxFreeReturnSum += fv * r;
            } else if (status === "Tax-Deferred 401k") {
              taxDeferredStartBal += fv;
              taxDeferredReturnSum += fv * r;
            } else {
              taxableStartBal += fv;
              taxableReturnSum += fv * r;
            }
          }
        });

        // Add pre-retirement illiquid asset sales to the starting taxable balance
        illiquidAssets.forEach(asset => {
          const saleYear = parseInt(asset.saleYear);
          if (saleYear < startYear + yearsToRetirement && asset.Strategy === "Sell to Fund") {
            const fvAtRetirement = parseFloat(asset.futureValue) || 0;
            const commission = parseFloat(asset.costToSell || 6.0) / 100;
            taxableStartBal += fvAtRetirement * (1 - commission);
          }
        });
      }

      // Calculate bucket-specific weighted average returns for the distribution phase
      const taxableAvgReturn = taxableStartBal > 0 ? (taxableReturnSum / taxableStartBal) || annualReturnRate : annualReturnRate;
      const taxDeferredAvgReturn = taxDeferredStartBal > 0 ? (taxDeferredReturnSum / taxDeferredStartBal) || annualReturnRate : annualReturnRate;
      const taxFreeAvgReturn = taxFreeStartBal > 0 ? (taxFreeReturnSum / taxFreeStartBal) || annualReturnRate : annualReturnRate;

      console.log(`[Retirement Start] Age ${desiredRetAge}: Taxable=$${taxableStartBal.toFixed(2)}, Tax-Deferred=$${taxDeferredStartBal.toFixed(2)}, Tax-Free=$${taxFreeStartBal.toFixed(2)}`);

      // ===================================================================
      // PHASE 2: DISTRIBUTION (Retirement Years)
      // ===================================================================

      // Input Object for calculateTotalTax
      const itemized =
        taxInfoAllData && taxInfoAllData.deductions === "itemized"
          ? {
            medical:
              (taxInfoAllData &&
                parseFloat(taxInfoAllData["Medical and Dental Expenses"])) ||
              0,
            salt:
              (taxInfoAllData &&
                parseFloat(taxInfoAllData["State and Local Taxes (SALT)"])) ||
              0,
            mortgage:
              (taxInfoAllData &&
                parseFloat(taxInfoAllData["Home Mortgage Interest"])) ||
              0,
            charity:
              (taxInfoAllData &&
                parseFloat(taxInfoAllData["Charitable Contributions"])) ||
              0,
          }
          : {};
      const taxInputs = {
        w2Income: (taxInfoAllData && Number(taxInfoAllData.grossIncome)) || 0,
        seNetIncome:
          (taxInfoAllData && Number(taxInfoAllData.earnedIncome)) || 0,
        filingStatus: taxInfoAllData && taxInfoAllData.taxFillingElection,
        state:
          (payload && payload["State of Residence in Retirement"]) ||
          (taxInfoAllData && taxInfoAllData.state),
        city: taxInfoAllData && taxInfoAllData.city,
        hsa: (taxInfoAllData && Number(taxInfoAllData.hsaContributions)) || 0,
        studentLoanInterest:
          (taxInfoAllData && Number(taxInfoAllData.studentLoan)) || 0,
        tradIra:
          (taxInfoAllData && Number(taxInfoAllData.iraContribution)) || 0,
        sepSimpleContributions: 0,
        educator:
          (taxInfoAllData && Number(taxInfoAllData.educatorExpenses)) || 0,
        dependentCareExp:
          (taxInfoAllData && Number(taxInfoAllData.careExpenses)) || 0,
        educationExp:
          (taxInfoAllData && Number(taxInfoAllData.educationExpenses)) || 0,
        dependents:
          (taxInfoAllData && Number(taxInfoAllData.noOfDependents)) || 0,
        deductionType: taxInfoAllData && taxInfoAllData.deductions,
        itemized,
        netShortTermCG:
          taxInfoAllData && taxInfoAllData.shortTermGainsLoss
            ? Number(taxInfoAllData.shortTermGainsLoss)
            : 0,
        netLongTermCG:
          taxInfoAllData && taxInfoAllData.longTermGainsLoss
            ? Number(taxInfoAllData.longTermGainsLoss)
            : 0,
        carryforward:
          taxInfoAllData && taxInfoAllData.lossCarryforward
            ? Number(taxInfoAllData.lossCarryforward)
            : 0,
      };

      const rows = [];

      for (let y = 0; y < yearsInRetirement; y++) {
        const year = startYear + yearsToRetirement + y;
        const age = desiredRetAge + y;

        // Spending need for this year (inflated from retirement start)
        const spendingNeed =
          annualPostRetBudget *
          Math.pow(1 + inflationRate, y);

        // Console log for Test Case 2 verification
        console.log(`[Retirement] Age ${age}: Annual Spending Need = $${spendingNeed.toFixed(2)}`);

        // Calculate current ages in this year for SS start checks
        const spouseAgeInYear = age - ageDiff;

        // Calculate conditional SS incomes based on start ages (inflation applied later)
        const ssUserFactor = age >= ssUserStartAge ? ssUser : 0;
        const ssSpouseFactor = spouseAgeInYear >= ssSpouseStartAge ? ssSpouse : 0;

        const additionalIncomesGrowthRates = payload["Annual Growth (COLA) %"] || [];
        const sumAdditionalIncomeGrown = additionalIncomes.reduce((sum, fv, idx) => {
          const rate = parseFloat(additionalIncomesGrowthRates[idx]) || 0;
          return sum + (parseFloat(fv) || 0) * Math.pow(1 + rate / 100, y);
        }, 0);

        const fixedIncomeTotal =
          (ssUserFactor + ssSpouseFactor + pension + rental) *
          Math.pow(1 + inflationRate, y) +
          sumAdditionalIncomeGrown;

        let portfolioWithdrawalNeed = Math.max(
          0,
          spendingNeed - fixedIncomeTotal
        );

        const taxableStart = taxableStartBal;
        const taxDeferredStart = taxDeferredStartBal;
        const taxFreeStart = taxFreeStartBal;

        // --------------------------------
        // ILLIQUID ASSET SALE (Test Case 3)
        // --------------------------------
        let assetSaleProceeds = 0;
        illiquidAssets.forEach(asset => {
          if (parseInt(asset.saleYear) === year && asset.Strategy === "Sell to Fund") {
            const netProceeds = parseFloat(asset.netLumpSum) || 0;
            assetSaleProceeds += netProceeds;
            console.log(`[Retirement] Age ${age}: Illiquid asset sold for $${netProceeds.toFixed(2)}`);
          }
        });

        // Add sale proceeds to taxable account
        taxableStartBal += assetSaleProceeds;

        // --------------------------------
        //  1) TAXABLE WITHDRAW
        // --------------------------------
        let taxableWithdraw = Math.min(portfolioWithdrawalNeed, taxableStartBal);
        let remainingNeed = portfolioWithdrawalNeed - taxableWithdraw;

        // --------------------------------
        //  2) RMD (Tax‐Deferred)
        // --------------------------------
        let rmd = 0;
        const rmdPeriod = rmdDistributionPeriods[age];
        if (age >= 73 && taxDeferredStartBal > 0 && rmdPeriod) {
          rmd = taxDeferredStartBal / rmdPeriod;
        }

        let taxDeferredWithdraw = Math.max(
          rmd,
          Math.min(remainingNeed, taxDeferredStartBal)
        );
        remainingNeed -= Math.min(taxDeferredWithdraw, remainingNeed);

        // --------------------------------
        //  3) ROTH WITHDRAW
        // --------------------------------
        let rothWithdraw = Math.min(remainingNeed, taxFreeStartBal);
        remainingNeed -= rothWithdraw;

        // --------------------------------
        //  GROWTH (Only on positive balances)
        // --------------------------------
        const taxableAfterWithdraw = Math.max(0, taxableStartBal - taxableWithdraw);
        const taxDeferredAfterWithdraw = Math.max(0, taxDeferredStartBal - taxDeferredWithdraw);
        const taxFreeAfterWithdraw = Math.max(0, taxFreeStartBal - rothWithdraw);

        const taxableGrowth = taxableAfterWithdraw * taxableAvgReturn;
        const taxDeferredGrowth = taxDeferredAfterWithdraw * taxDeferredAvgReturn;
        const rothGrowth = taxFreeAfterWithdraw * taxFreeAvgReturn;

        const taxableEnd = taxableAfterWithdraw + taxableGrowth;
        const taxDeferredEnd = taxDeferredAfterWithdraw + taxDeferredGrowth;
        const rothEnd = taxFreeAfterWithdraw + rothGrowth;

        const totalWithdrawals =
          taxableWithdraw + taxDeferredWithdraw + rothWithdraw;

        const totalPreTaxIncome =
          fixedIncomeTotal + taxDeferredWithdraw + taxableWithdraw;

        const taxInputsForYear = {
          ...taxInputs,
          w2Income: totalPreTaxIncome,
          filingStatus,
          age,
          spouseAge,
        };

        const yearTaxResult = calculateTotalTax(taxInputsForYear);

        const federalTaxes = (yearTaxResult && yearTaxResult.fedTax) || 0;
        const stateTaxes = (yearTaxResult && yearTaxResult.stateTax) || 0;

        const totalEndingBalance = taxableEnd + taxDeferredEnd + rothEnd;

        rows.push({
          year: year,
          age: age,
          spouseAge: spouseAgeInYear,
          retirementStatus: "Retired",
          annualSpendingNeed: spendingNeed,
          // socialSecurityIncome:
          // (ssUser + ssSpouse) *
          //   Math.pow(1 + inflationRate, y),
          // pensionIncome:
          //   pension * Math.pow(1 + inflationRate, y),
          // otherIncome:
          //   (rental + sumAdditionalIncome) *
          //   Math.pow(1 + inflationRate, y),
          socialSecurityIncome: ssUserFactor * Math.pow(1 + inflationRate, y),
          socialSecurityIncomeSpouse: ssSpouseFactor * Math.pow(1 + inflationRate, y),
          pensionIncome: pension * Math.pow(1 + inflationRate, y),
          otherIncome: (rental * Math.pow(1 + inflationRate, y)) + sumAdditionalIncomeGrown,
          portfolioWithdrawalNeed: portfolioWithdrawalNeed,
          taxableStart: taxableStart + assetSaleProceeds, // Include sale proceeds
          taxableGrowth: taxableGrowth,
          taxableWithdrawals: taxableWithdraw,
          taxableEnd: taxableEnd,
          taxDeferredStart: taxDeferredStart,
          taxDeferredGrowth: taxDeferredGrowth,
          rmdCalculation: rmd,
          taxDeferredWithdrawals: taxDeferredWithdraw,
          taxDeferredEnd: taxDeferredEnd,
          rothStart: taxFreeStart,
          rothGrowth: rothGrowth,
          rothWithdrawals: rothWithdraw,
          rothEnd: rothEnd,
          totalWithdrawals: totalWithdrawals,
          totalPreTaxIncome: totalPreTaxIncome,
          estimatedStateTaxesPaid: stateTaxes,
          estimatedFederalTaxesPaid: federalTaxes,
          totalEndingBalance: totalEndingBalance,
        });

        // prepare balances for next year
        taxableStartBal = taxableEnd;
        taxDeferredStartBal = taxDeferredEnd;
        taxFreeStartBal = rothEnd;
      }

      return rows;
    }

    const projectionRows = runFullProjection();

    // ===================================================================
    // CALCULATE ACTUAL DEPLETION AGE (Test Case 1 Fix)
    // ===================================================================
    let fundsLastUntilAge = lifeExpectancy; // Default: funds last until life expectancy
    let depletedYear = null;

    for (let i = 0; i < projectionRows.length; i++) {
      const row = projectionRows[i];
      if (row.totalEndingBalance <= 0) {
        fundsLastUntilAge = row.age;
        depletedYear = row.year;
        console.log(`[Depletion] Funds depleted at Age ${fundsLastUntilAge} (Year ${depletedYear})`);
        break;
      }
    }

    // If we never hit zero, funds last beyond life expectancy
    if (!depletedYear) {
      console.log(`[Success] Funds last until Age ${lifeExpectancy}+ (Goal Met!)`);
    }

    // Calculate percent funded for display purposes
    let percentFunded = 0;
    if (fundsLastUntilAge >= lifeExpectancy) {
      percentFunded = 100; // Fully funded
    } else {
      const yearsAchieved = Math.max(0, fundsLastUntilAge - desiredRetAge);
      const yearsNeeded = Math.max(1, lifeExpectancy - desiredRetAge);
      percentFunded = (yearsAchieved / yearsNeeded) * 100;
    }

    // NEW PAYLOAD VALUES
    const updatedValues = {
      "Current Monthly Gap": requiredMonthly,
      "Suggested Savings": suggestedSavings,
      "Remaining Monthly Gap": remainingGap,
      "Funds Last Until Age": fundsLastUntilAge, // Now returns actual age, not percentage
      "Percent Funded": percentFunded.toFixed(2), // Add this for reference
    };
    console.log("Updated Values:", updatedValues);
    // ONLY SET PAYLOAD IF SOMETHING CHANGED
    if (hasChanges(payload, updatedValues)) {
      setPayload((prev) => ({ ...prev, ...updatedValues }));
    }
    setProjectionTable(projectionRows);
  }, [payload, taxInfoAllData, assetsAssign, rateOfReturnData]);

  return (
    <div className="ret-projection">
      <h2 className="ret-title">Retirement Plan Results</h2>

      {/* Summary Section */}
      <div className="ret-summary">
        <div className="summary-grid">
          <div className="summary-item">
            <label>Total Savings Goal at Retirement:</label>
            <strong>{formatCurrencyAmount(goalsValue)}</strong>
          </div>

          <div className="summary-item">
            <label>Projected Value (with current savings):</label>
            <strong>{formatCurrencyAmount(userEstimatedValue)}</strong>
          </div>

          <div className="summary-item highlight">
            <label>Required Additional Monthly Savings:</label>
            <strong>{formatCurrencyAmount(userMonthly)}</strong>
          </div>

          <div className="summary-item">
            <label>Your Proposed Monthly Savings:</label>
            <strong>
              {formatCurrencyAmount(proposedMonthlySavings || userMonthly)}
            </strong>
          </div>

          <div className="summary-item full-width">
            <label>Suggested Allocation (monthly):</label>
            <div className="allocation-box">
              Tax-Deferred:{" "}
              <strong>
                {formatCurrencyAmount(savingsAllocationMonthly.deferred)}
              </strong>{" "}
              / mo
              <br />
              Tax-Free:{" "}
              <strong>
                {formatCurrencyAmount(savingsAllocationMonthly.free)}
              </strong>{" "}
              / mo
              <br />
              Taxable:{" "}
              <strong>
                {formatCurrencyAmount(savingsAllocationMonthly.taxable)}
              </strong>{" "}
              / mo
            </div>
          </div>

          <div className="summary-item">
            <label>Plan Net After Tax (annual):</label>
            <strong className="text-success">
              {formatCurrencyAmount(netAfterTax)}
            </strong>
          </div>
        </div>
      </div>

      <hr className="divider" />

      <h3 className="section-title">Annual Projection (Based on Your Plan)</h3>

      {/* Table */}
      <div className="table-container">
        <table className="projection-table">
          <thead>
            <tr>
              <th rowSpan="2" style={{ verticalAlign: "middle", textAlign: "center", backgroundColor: "#2a3da3", color: "#ffffff", border: "1px solid #ffffff" }}>Year</th>
              <th rowSpan="2" style={{ verticalAlign: "middle", textAlign: "center", backgroundColor: "#2a3da3", color: "#ffffff", border: "1px solid #ffffff" }}>Age</th>
              <th rowSpan="2" style={{ verticalAlign: "middle", textAlign: "center", backgroundColor: "#2a3da3", color: "#ffffff", border: "1px solid #ffffff" }}>Spouse Age</th>
              <th rowSpan="2" style={{ verticalAlign: "middle", textAlign: "center", backgroundColor: "#2a3da3", color: "#ffffff", border: "1px solid #ffffff" }}>Annual Spending Need (Inflated)</th>

              <th colSpan="4" style={{ textAlign: "center", backgroundColor: "#2a3da3", color: "#ffffff", border: "1px solid #ffffff" }}>Income Sources</th>
              <th rowSpan="2" style={{ verticalAlign: "middle", textAlign: "center", backgroundColor: "#2a3da3", color: "#ffffff", border: "1px solid #ffffff" }}>Portfolio Withdrawal Need</th>

              <th colSpan="4" style={{ textAlign: "center", backgroundColor: "#2a3da3", color: "#ffffff", border: "1px solid #ffffff" }}>Taxable Assets</th>
              <th colSpan="5" style={{ textAlign: "center", backgroundColor: "#2a3da3", color: "#ffffff", border: "1px solid #ffffff" }}>Tax-Deferred Assets</th>
              <th colSpan="4" style={{ textAlign: "center", backgroundColor: "#2a3da3", color: "#ffffff", border: "1px solid #ffffff" }}>Tax-Free Assets (Roth)</th>

              <th colSpan="5" style={{ textAlign: "center", backgroundColor: "#2a3da3", color: "#ffffff", border: "1px solid #ffffff" }}>Summary & Taxes</th>
            </tr>
            <tr>
              {/* Income Sources Columns */}
              <th style={{ backgroundColor: "#2a3da3", color: "#ffffff", border: "1px solid #ffffff", fontSize: "0.9rem" }}>Social Security</th>
              <th style={{ backgroundColor: "#2a3da3", color: "#ffffff", border: "1px solid #ffffff", fontSize: "0.9rem" }}>SS - Spouse</th>
              <th style={{ backgroundColor: "#2a3da3", color: "#ffffff", border: "1px solid #ffffff", fontSize: "0.9rem" }}>Pension</th>
              <th style={{ backgroundColor: "#2a3da3", color: "#ffffff", border: "1px solid #ffffff", fontSize: "0.9rem" }}>Other Income</th>

              {/* Taxable Columns */}
              <th style={{ backgroundColor: "#2a3da3", color: "#ffffff", border: "1px solid #ffffff", fontSize: "0.9rem" }}>Start Balance</th>
              <th style={{ backgroundColor: "#2a3da3", color: "#ffffff", border: "1px solid #ffffff", fontSize: "0.9rem" }}>Growth</th>
              <th style={{ backgroundColor: "#2a3da3", color: "#ffffff", border: "1px solid #ffffff", fontSize: "0.9rem" }}>Withdrawals</th>
              <th style={{ backgroundColor: "#2a3da3", color: "#ffffff", border: "1px solid #ffffff", fontSize: "0.9rem" }}>End Balance</th>

              {/* Tax-Deferred Columns */}
              <th style={{ backgroundColor: "#2a3da3", color: "#ffffff", border: "1px solid #ffffff", fontSize: "0.9rem" }}>Start Balance</th>
              <th style={{ backgroundColor: "#2a3da3", color: "#ffffff", border: "1px solid #ffffff", fontSize: "0.9rem" }}>Growth</th>
              <th style={{ backgroundColor: "#2a3da3", color: "#ffffff", border: "1px solid #ffffff", fontSize: "0.9rem" }}>RMD</th>
              <th style={{ backgroundColor: "#2a3da3", color: "#ffffff", border: "1px solid #ffffff", fontSize: "0.9rem" }}>Withdrawals</th>
              <th style={{ backgroundColor: "#2a3da3", color: "#ffffff", border: "1px solid #ffffff", fontSize: "0.9rem" }}>End Balance</th>

              {/* Tax-Free Columns */}
              <th style={{ backgroundColor: "#2a3da3", color: "#ffffff", border: "1px solid #ffffff", fontSize: "0.9rem" }}>Start Balance</th>
              <th style={{ backgroundColor: "#2a3da3", color: "#ffffff", border: "1px solid #ffffff", fontSize: "0.9rem" }}>Growth</th>
              <th style={{ backgroundColor: "#2a3da3", color: "#ffffff", border: "1px solid #ffffff", fontSize: "0.9rem" }}>Withdrawals</th>
              <th style={{ backgroundColor: "#2a3da3", color: "#ffffff", border: "1px solid #ffffff", fontSize: "0.9rem" }}>End Balance</th>

              {/* Summary Columns */}
              <th style={{ backgroundColor: "#2a3da3", color: "#ffffff", border: "1px solid #ffffff", fontSize: "0.9rem" }}>Total Withdrawals</th>
              <th style={{ backgroundColor: "#2a3da3", color: "#ffffff", border: "1px solid #ffffff", fontSize: "0.9rem" }}>Total Pre-Tax Income</th>
              <th style={{ backgroundColor: "#2a3da3", color: "#ffffff", border: "1px solid #ffffff", fontSize: "0.9rem" }}>State Tax</th>
              <th style={{ backgroundColor: "#2a3da3", color: "#ffffff", border: "1px solid #ffffff", fontSize: "0.9rem" }}>Fed Tax</th>
              <th style={{ backgroundColor: "#2a3da3", color: "#ffffff", border: "1px solid #ffffff", fontSize: "0.9rem" }}>Total Ending Balance</th>
            </tr>
          </thead>
          <tbody>
            {projectionTable.map((r, i) => (
              <tr key={i}>
                <td className="text-center">{r.year}</td>
                <td className="text-center">{r.age}</td>
                <td className="text-center">{r.spouseAge}</td>
                {/* <td className="text-center status">{r.retirementStatus}</td> */}
                <td className="text-center">
                  {formatCurrencyAmount(r.annualSpendingNeed)}
                </td>
                <td className="text-center">
                  {formatCurrencyAmount(r.socialSecurityIncome)}
                </td>
                <td className="text-center">
                  {formatCurrencyAmount(r.socialSecurityIncomeSpouse)}
                </td>
                <td className="text-center">
                  {formatCurrencyAmount(r.pensionIncome)}
                </td>
                <td className="text-center">
                  {formatCurrencyAmount(r.otherIncome)}
                </td>
                <td className="text-center text-bold text-danger">
                  {formatCurrencyAmount(r.portfolioWithdrawalNeed)}
                </td>

                {/* Taxable */}
                <td className="text-center">
                  {formatCurrencyAmount(r.taxableStart)}
                </td>
                <td className="text-center text-success">
                  {formatCurrencyAmount(r.taxableGrowth)}
                </td>
                <td className="text-center text-danger">
                  {formatCurrencyAmount(r.taxableWithdrawals)}
                </td>
                <td className="text-center text-bold">
                  {formatCurrencyAmount(r.taxableEnd)}
                </td>

                {/* Tax-Deferred */}
                <td className="text-center">
                  {formatCurrencyAmount(r.taxDeferredStart)}
                </td>
                <td className="text-center text-success">
                  {formatCurrencyAmount(r.taxDeferredGrowth)}
                </td>
                <td className="text-center">
                  {r.rmdCalculation > 0
                    ? formatCurrencyAmount(r.rmdCalculation)
                    : "-"}
                </td>
                <td className="text-center text-danger">
                  {formatCurrencyAmount(r.taxDeferredWithdrawals)}
                </td>
                <td className="text-center text-bold">
                  {formatCurrencyAmount(r.taxDeferredEnd)}
                </td>

                {/* Roth */}
                <td className="text-center">
                  {formatCurrencyAmount(r.rothStart)}
                </td>
                <td className="text-center text-success">
                  {formatCurrencyAmount(r.rothGrowth)}
                </td>
                <td className="text-center text-danger">
                  {formatCurrencyAmount(r.rothWithdrawals)}
                </td>
                <td className="text-center text-bold">
                  {formatCurrencyAmount(r.rothEnd)}
                </td>

                {/* Summary */}
                <td className="text-center text-bold">
                  {formatCurrencyAmount(r.totalWithdrawals)}
                </td>
                <td className="text-center">
                  {formatCurrencyAmount(r.totalPreTaxIncome)}
                </td>
                <td className="text-center text-warning">
                  {formatCurrencyAmount(r.estimatedStateTaxesPaid)}
                </td>
                <td className="text-center text-warning">
                  {formatCurrencyAmount(r.estimatedFederalTaxesPaid)}
                </td>
                <td className="text-center text-bold text-large">
                  {formatCurrencyAmount(r.totalEndingBalance)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <WithdrawalStrategyChart
        projectionTable={projectionTable && projectionTable}
      />
    </div>
  );
}
